import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import cloneDeep from 'lodash.clonedeep';
import { BackupFileDTO, BackupJobDTO, EnvVariable } from 'data/backupRestore/backup.types';
import * as backupActions from 'data/backupRestore/backup.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as backupSelectors from 'data/backupRestore/backup.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AppToaster } from 'shared/toaster';
import BackupRestoreGrid from './components/BackupRestoreGrid';
import { backupTasksColumnDefs, backupFilesColumnDefs } from '../../backupRestore.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  userId: authenticationSelectors.getAuthenticationUserId(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  backupTasks: backupSelectors.getAllBackupRestoreTasks(state),
  userDBId: state.authentication.userDBId,
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
});

const mapDispatch = {
  goToBackupCreate: backupActions.goToBackupCreate,
  getAllBackupTasks: backupActions.getAllBackupTasks,
  deleteBackupFile: backupActions.deleteBackupFile,
  deleteJob: backupActions.deleteJob,
  restoreBackupFile: backupActions.restoreBackupFile,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const BackupRestoreList: FunctionComponent<Props> = ({
  userId,
  history,
  beforeLogout,
  deliveryFunctionId,
  backupTasks,
  goToBackupCreate,
  getAllBackupTasks,
  deleteBackupFile,
  restoreBackupFile,
  deleteJob
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [backupFilesGridData, setBackupFilesGrid] = useState<BackupFileDTO[] | []>([]);
  const [backupTasksGrid, setBackupTasksGrid] = useState<BackupJobDTO[] | []>([]);
  const [selectedBackupFile, setSelectedBackupFile] = useState<BackupFileDTO>();
  const [selectedJob, setSelectedJob] = useState<BackupJobDTO>();

  useEffect(() => {
    getAllBackupTasks();

    return () => {
      AppToaster.clear();
    };
  }, [
    deliveryFunctionId,
    getAllBackupTasks
  ]);

  useEffect(() => {
    if (backupTasks) {
      const clonedBackupTasks = cloneDeep(backupTasks);

      const adaptedClonedBackupTasks: BackupJobDTO[] = [];
      clonedBackupTasks.xcipioJobList?.forEach((job) => adaptedClonedBackupTasks.push({
        ...job,
        product: 'xcipio',
        start_time: job.start_time ? new Date(Number(job.start_time) * 1000) : null,
        completion_time: job.completion_time ? new Date(Number(job.completion_time) * 1000) : null
      }));
      clonedBackupTasks.dxJobList?.forEach((job) => adaptedClonedBackupTasks.push({
        ...job,
        product: 'discovery',
        start_time: job.start_time ? new Date(Number(job.start_time) * 1000) : null,
        completion_time: job.completion_time ? new Date(Number(job.completion_time) * 1000) : null
      }));

      adaptedClonedBackupTasks.sort((task1, task2) => {
        if (task1.start_time === null && task2.start_time === null) {
          return 0;
        }
        if (task1.start_time === null) {
          return -1;
        }
        if (task2.start_time === null) {
          return 1;
        }
        return task2.start_time.getTime() - task1.start_time.getTime();
      });

      const backupFiles: BackupFileDTO[] = [];
      clonedBackupTasks.xcipioBackupFilesList?.forEach((file) => backupFiles.push({
        ...file,
        product: 'xcipio',
      }));
      clonedBackupTasks.dxBackupFilesList?.forEach((file) => backupFiles.push({
        ...file,
        product: 'discovery',
      }));

      setBackupTasksGrid(adaptedClonedBackupTasks);
      setBackupFilesGrid(backupFiles);
    }

  }, [backupTasks]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [backupTasks]);

  const handleRefreshBackupFiles = () => {
    getAllBackupTasks();
  }
  const handleRefreshJobs = () => {
    getAllBackupTasks();
  };

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToBackupCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    setSelectedBackupFile(event.api.getSelectedRows()[0]);
  };

  const handleShowActionMenuItem = () => true;

  const handleEdit = () => { };

  const handleRestore = (shouldRecreateDB = false) => {
    if (selectedBackupFile) {
      const payload = {
        FILENAME: selectedBackupFile.filename,
        CREATE_DATABASE: shouldRecreateDB.toString(),
        owner: userId,
        product: selectedBackupFile.product
      }
      restoreBackupFile(payload);
    }
  };

  const handleSelectItemMenuAction = () => { };

  const handleDelete = () => {
    if (selectedBackupFile) {
      deleteBackupFile(selectedBackupFile.filename, selectedBackupFile.product);
      setSelectedBackupFile(undefined);
    }
  };

  const handleJobSelectionChanged = (event: SelectionChangedEvent) => {
    setSelectedJob(event.api.getSelectedRows()[0]);
  };

  const handleDeleteJob = () => {
    if (selectedJob) {
      deleteJob(selectedJob.product || '', selectedJob.id);
      setSelectedJob(undefined);
    }
  }

  return (
    <Fragment>
      <div data-testid="BackupRestoreList" className="GridPageContainerScroll">
        <p className="titleHeader">Backup Files</p>
        <div data-testid="BackupFilesList" className="GridPageContainerSmall">
          <BackupRestoreGrid
            data={backupFilesGridData}
            gridContent="files"
            hasPrivilegeToView={false}
            hasPrivilegesToCreate
            hasPrivilegeToDelete
            noResults={false}
            beforeLogout={beforeLogout}
            columnDefs={backupFilesColumnDefs}
            onRefresh={handleRefreshBackupFiles}
            onCreateNew={handleCreateNew}
            onSelectionChanged={handleSelectionChanged}
            onSelectItemMenuAction={handleSelectItemMenuAction}
            onShowActionMenuItem={handleShowActionMenuItem}
            lastUpdatedAt={lastUpdatedAt}
            selectedBackupFiles={selectedBackupFile}
            onEdit={handleEdit}
            onRestore={handleRestore}
            groupDisplayType="custom"
            groupDefaultExpanded={1}
          />
        </div>
        <p className="titleHeader">Jobs</p>
        <div data-testid="BackupTasksList" className="GridPageContainerSmall">
          <BackupRestoreGrid
            data={backupTasksGrid}
            gridContent="tasks"
            hasPrivilegeToView={false}
            hasPrivilegesToCreate={false}
            hasPrivilegeToDelete={false}
            noResults={false}
            beforeLogout={beforeLogout}
            columnDefs={backupTasksColumnDefs}
            onRefresh={handleRefreshJobs}
            onCreateNew={handleCreateNew}
            onSelectionChanged={handleJobSelectionChanged}
            onSelectItemMenuAction={handleSelectItemMenuAction}
            onShowActionMenuItem={handleShowActionMenuItem}
            lastUpdatedAt={lastUpdatedAt}
            selectedJob={selectedJob}
            onDeleteRow={handleDeleteJob}
            onEdit={handleEdit}
            groupDisplayType="custom"
            groupDefaultExpanded={1}
          />
        </div>
      </div>
    </Fragment>
  );
};

export default withRouter(connector(BackupRestoreList));
